import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react';
import clsx from 'clsx';
import { Button } from '../../components/Button';
import { AUTH_TYPES, CYBERSOURCE_AUTH_TYPES } from './constants';
import type { ProfileFormData } from './hooks/useProxyProfiles';
import styles from './ProxyConfig.module.css';

interface ProfileEditorProps {
  profile: any;
  formData: ProfileFormData;
  onChange: (data: ProfileFormData) => void;
  testUrl: string;
  onTestUrlChange: (url: string) => void;
  onSaveTestUrl: () => void;
  onPortalChange: (mode: string) => void;
  onSave: (e: FormEvent) => void;
  onBack: () => void;
  onTestAuth: (profile: any) => void;
  onTestMle: (profile: any) => void;
  authTestResults: Record<string, any>;
  mleTestResults: Record<string, any>;
  onFileUpload: (field: string, file: File) => void;
  onMleFileUpload: (field: string, file: File) => void;
  onP12Upload: (e: ChangeEvent<HTMLInputElement>) => void;
}

export const ProfileEditor: FC<ProfileEditorProps> = ({
  profile,
  formData,
  onChange,
  testUrl,
  onTestUrlChange,
  onSaveTestUrl,
  onPortalChange,
  onSave,
  onBack,
  onTestAuth,
  onTestMle,
  authTestResults,
  mleTestResults,
  onFileUpload,
  onMleFileUpload,
  onP12Upload,
}) => {
  const renderAuthFields = (): ReactNode => {
    switch (formData.authType) {
      case 'mtls':
        return (
          <>
            <div className={styles.formGroup}>
              <label className={styles.label}>User ID</label>
              <input
                className={styles.input}
                value={formData.authConfig.userId || ''}
                onChange={(e) =>
                  onChange({ ...formData, authConfig: { ...formData.authConfig, userId: e.target.value } })
                }
                placeholder="Your User ID"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Password</label>
              <input
                className={styles.input}
                type="password"
                value={formData.authConfig.password || ''}
                onChange={(e) =>
                  onChange({ ...formData, authConfig: { ...formData.authConfig, password: e.target.value } })
                }
                placeholder="Your Password"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Client Certificate (PEM)</label>
              <textarea
                className={styles.textarea}
                value={formData.authConfig.cert || ''}
                onChange={(e) =>
                  onChange({ ...formData, authConfig: { ...formData.authConfig, cert: e.target.value } })
                }
                placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
              />
              <Button
                variant="secondary"
                type="button"
                onClick={() => document.getElementById('cert-file')?.click()}
                className={styles.uploadButton}
              >
                Upload Certificate File
              </Button>
              <input
                id="cert-file"
                type="file"
                accept=".pem,.crt"
                className={styles.hiddenInput}
                onChange={(e) => e.target.files?.[0] && onFileUpload('cert', e.target.files[0])}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Private Key (PEM)</label>
              <textarea
                className={styles.textarea}
                value={formData.authConfig.key || ''}
                onChange={(e) => onChange({ ...formData, authConfig: { ...formData.authConfig, key: e.target.value } })}
                placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
              />
              <Button
                variant="secondary"
                type="button"
                onClick={() => document.getElementById('key-file')?.click()}
                className={styles.uploadButton}
              >
                Upload Private Key File
              </Button>
              <input
                id="key-file"
                type="file"
                accept=".pem,.key"
                className={styles.hiddenInput}
                onChange={(e) => e.target.files?.[0] && onFileUpload('key', e.target.files[0])}
              />
            </div>
          </>
        );

      case 'xpay':
        return (
          <>
            <div className={styles.formGroup}>
              <label className={styles.label}>API Key</label>
              <input
                className={styles.input}
                value={formData.authConfig.api_key || ''}
                onChange={(e) =>
                  onChange({ ...formData, authConfig: { ...formData.authConfig, api_key: e.target.value } })
                }
                placeholder="Your API Key"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Shared Secret</label>
              <input
                className={styles.input}
                type="password"
                value={formData.authConfig.shared_secret || ''}
                onChange={(e) =>
                  onChange({ ...formData, authConfig: { ...formData.authConfig, shared_secret: e.target.value } })
                }
                placeholder="Your Shared Secret"
              />
            </div>
          </>
        );

      default:
        return null;
    }
  };

  const renderTestResultBadge = (results: Record<string, any>, label: string) => {
    const result = results[profile.id];
    if (!result || result.loading) return null;

    return (
      <div className={styles.testResult} data-success={result.success ? 'true' : 'false'}>
        <strong>{result.success ? `✓ ${label} Successful` : `✗ ${label} Failed`}</strong>
        <p>{result.message}</p>
        {result.error && <p>Error: {result.error}</p>}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.profileEditorTopBar}>
        <Button variant="secondary" onClick={onBack}>
          ← Back to Profiles
        </Button>
        <Button variant="success" onClick={onSave}>
          Save Changes
        </Button>
      </div>

      <form onSubmit={onSave}>
        {/* Profile Information */}
        <div className={styles.configSection}>
          <div className={styles.configSectionHeader}>
            <h3>Profile Information</h3>
            <p>Basic profile details</p>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Profile Name *</label>
            <input
              className={styles.input}
              value={formData.name}
              onChange={(e) => onChange({ ...formData, name: e.target.value })}
              placeholder="My Authentication Profile"
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Description</label>
            <textarea
              className={styles.textareaShort}
              value={formData.description}
              onChange={(e) => onChange({ ...formData, description: e.target.value })}
              placeholder="Profile description"
            />
          </div>
        </div>

        {/* Mode Configuration */}
        <div className={styles.configSection}>
          <div className={styles.configSectionHeader}>
            <h3>Mode Configuration</h3>
            <p>Select connection mode for API requests</p>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Mode *</label>
            <select
              className={styles.select}
              value={formData.portal}
              onChange={(e) => onPortalChange(e.target.value)}
              required
            >
              <option value="">-- Select Mode --</option>
              <option value="vdp">Visa Developer Platform (VDP)</option>
              <option value="cybersource">CyberSource</option>
              <option value="authorize">Authorize.Net</option>
            </select>
          </div>

          {formData.portal && formData.portal !== 'direct' && (
            <div className={styles.formGroup}>
              <label className={styles.label}>Test URL *</label>
              <div className={styles.inputRow}>
                <input
                  className={clsx(styles.input, styles.inputRowFlex)}
                  value={testUrl}
                  onChange={(e) => onTestUrlChange(e.target.value)}
                  placeholder="https://sandbox.api.visa.com/"
                  required
                />
                <Button variant="primary" onClick={onSaveTestUrl}>
                  Save
                </Button>
              </div>
              <small className={styles.helpText}>
                Base URL where API requests will be sent (e.g., https://sandbox.api.visa.com/)
              </small>
            </div>
          )}
        </div>

        {/* Portal-specific sections */}
        {!formData.portal ? (
          <div className={styles.configSection}>
            <div className={styles.configSectionHeader}>
              <h3>Mode Not Configured</h3>
              <p>Please select a mode to configure authentication</p>
            </div>
            <div className={styles.modeNotice}>
              <p>Select a mode from the dropdown above to configure this profile.</p>
              <p>Available modes: VDP, CyberSource, Authorize.Net</p>
            </div>
          </div>
        ) : formData.portal === 'direct' ? (
          <div className={styles.configSection}>
            <div className={styles.configSectionHeader}>
              <h3>Direct Mode</h3>
              <p>Traffic will pass directly without proxy or authentication</p>
            </div>
            <div className={styles.modeNotice}>
              <p>No additional configuration required for Direct mode.</p>
              <p>All requests will be sent directly to the target without modifications.</p>
            </div>
          </div>
        ) : formData.portal === 'authorize' ? (
          <div className={styles.configSection}>
            <div className={styles.configSectionHeader}>
              <h3>Authentication</h3>
              <p>Configure Authorize.Net authentication</p>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>API Login ID *</label>
              <input
                className={styles.input}
                value={formData.authConfig.apiLoginId || ''}
                onChange={(e) =>
                  onChange({ ...formData, authConfig: { ...formData.authConfig, apiLoginId: e.target.value } })
                }
                placeholder="Your API Login ID"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Transaction Key *</label>
              <input
                className={styles.input}
                type="password"
                value={formData.authConfig.transactionKey || ''}
                onChange={(e) =>
                  onChange({ ...formData, authConfig: { ...formData.authConfig, transactionKey: e.target.value } })
                }
                placeholder="Your Transaction Key"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <Button type="button" variant="success" onClick={() => onTestAuth(profile)}>
                Test Authentication
              </Button>
              {renderTestResultBadge(authTestResults, 'Auth Test')}
            </div>
          </div>
        ) : formData.portal === 'vdp' ? (
          <>
            <div className={styles.configSection}>
              <div className={styles.configSectionHeader}>
                <h3>Authentication</h3>
                <p>Configure authentication method for API requests</p>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Authentication Type *</label>
                <select
                  className={styles.select}
                  value={formData.authType}
                  onChange={(e) => onChange({ ...formData, authType: e.target.value, authConfig: {} })}
                  required
                >
                  {AUTH_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {renderAuthFields()}

              <div className={styles.formGroup}>
                <Button type="button" variant="success" onClick={() => onTestAuth(profile)}>
                  Test Authentication
                </Button>
                {renderTestResultBadge(authTestResults, 'Auth Test')}
              </div>
            </div>

            {/* MLE Configuration */}
            <div className={styles.configSection}>
              <div className={styles.configSectionHeader}>
                <h3>MLE Configuration</h3>
                <p>Configure Message Level Encryption settings</p>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Key ID</label>
                <input
                  className={styles.input}
                  value={formData.mleConfig.keyId}
                  onChange={(e) =>
                    onChange({ ...formData, mleConfig: { ...formData.mleConfig, keyId: e.target.value } })
                  }
                  placeholder="MLE Key ID"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Server Certificate (PEM) - for encryption</label>
                <textarea
                  className={styles.textarea}
                  value={formData.mleConfig.serverCertificate}
                  onChange={(e) =>
                    onChange({ ...formData, mleConfig: { ...formData.mleConfig, serverCertificate: e.target.value } })
                  }
                  placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                />
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => document.getElementById('mle-server-cert')?.click()}
                  className={styles.uploadButton}
                >
                  Upload Server Certificate
                </Button>
                <input
                  id="mle-server-cert"
                  type="file"
                  accept=".pem,.crt"
                  className={styles.hiddenInput}
                  onChange={(e) => e.target.files?.[0] && onMleFileUpload('serverCertificate', e.target.files[0])}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Private Key (PEM) - for decryption</label>
                <textarea
                  className={styles.textarea}
                  value={formData.mleConfig.privateKey}
                  onChange={(e) =>
                    onChange({ ...formData, mleConfig: { ...formData.mleConfig, privateKey: e.target.value } })
                  }
                  placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
                />
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => document.getElementById('mle-private-key')?.click()}
                  className={styles.uploadButton}
                >
                  Upload Private Key
                </Button>
                <input
                  id="mle-private-key"
                  type="file"
                  accept=".pem,.key"
                  className={styles.hiddenInput}
                  onChange={(e) => e.target.files?.[0] && onMleFileUpload('privateKey', e.target.files[0])}
                />
              </div>

              <div className={styles.formGroup}>
                <Button
                  type="button"
                  variant="success"
                  onClick={() => onTestMle(profile)}
                  disabled={!formData.mleConfig.keyId}
                >
                  Test MLE Configuration
                </Button>
                <small className={styles.helpText}>
                  Will test using: {testUrl}visadirect/fundstransfer/v1/pushfundstransactions
                </small>
                {renderTestResultBadge(mleTestResults, 'MLE Test')}
              </div>
            </div>
          </>
        ) : formData.portal === 'cybersource' ? (
          <div className={styles.configSection}>
            <div className={styles.configSectionHeader}>
              <h3>CyberSource Authentication</h3>
              <p>Configure CyberSource authentication method</p>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Authentication Type *</label>
              <select
                className={styles.select}
                value={formData.authType}
                onChange={(e) => onChange({ ...formData, authType: e.target.value, authConfig: {} })}
                required
              >
                <option value="">-- Select Authentication Type --</option>
                {CYBERSOURCE_AUTH_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {formData.authType === 'http_signature' && (
              <>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Merchant ID *</label>
                  <input
                    className={styles.input}
                    value={formData.authConfig.merchantId || ''}
                    onChange={(e) =>
                      onChange({ ...formData, authConfig: { ...formData.authConfig, merchantId: e.target.value } })
                    }
                    placeholder="Your Merchant ID"
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Merchant Key ID *</label>
                  <input
                    className={styles.input}
                    value={formData.authConfig.merchantKeyId || ''}
                    onChange={(e) =>
                      onChange({ ...formData, authConfig: { ...formData.authConfig, merchantKeyId: e.target.value } })
                    }
                    placeholder="Your Key ID (e.g., 08c94330-f618-42a3-b09d-e1e43be5efda)"
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Merchant Secret Key *</label>
                  <input
                    className={styles.input}
                    type="password"
                    value={formData.authConfig.merchantSecretKey || ''}
                    onChange={(e) =>
                      onChange({
                        ...formData,
                        authConfig: { ...formData.authConfig, merchantSecretKey: e.target.value },
                      })
                    }
                    placeholder="Your Secret Key (Base64 encoded)"
                    required
                  />
                </div>
              </>
            )}

            {formData.authType === 'jwt' && (
              <>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Merchant ID *</label>
                  <input
                    className={styles.input}
                    value={formData.authConfig.merchantId || ''}
                    onChange={(e) =>
                      onChange({ ...formData, authConfig: { ...formData.authConfig, merchantId: e.target.value } })
                    }
                    placeholder="Your Merchant ID"
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Key Alias *</label>
                  <input
                    className={styles.input}
                    value={formData.authConfig.keyAlias || ''}
                    onChange={(e) =>
                      onChange({ ...formData, authConfig: { ...formData.authConfig, keyAlias: e.target.value } })
                    }
                    placeholder="Your Key Alias (e.g., testrest)"
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Key Password *</label>
                  <input
                    className={styles.input}
                    type="password"
                    value={formData.authConfig.keyPassword || ''}
                    onChange={(e) =>
                      onChange({ ...formData, authConfig: { ...formData.authConfig, keyPassword: e.target.value } })
                    }
                    placeholder="Your Key Password"
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>P12 Key File *</label>
                  <div className={styles.fileStatus}>
                    {formData.authConfig.keyFileContent
                      ? `File uploaded: ${formData.authConfig.keyFilename || 'key.p12'}`
                      : 'No file uploaded'}
                  </div>
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={() => document.getElementById('cybersource-p12-file')?.click()}
                  >
                    {formData.authConfig.keyFileContent ? 'Change P12 Key File' : 'Upload P12 Key File'}
                  </Button>
                  <input
                    id="cybersource-p12-file"
                    type="file"
                    accept=".p12"
                    className={styles.hiddenInput}
                    onChange={onP12Upload}
                  />
                </div>
              </>
            )}

            {formData.authType && (
              <div className={styles.formGroup}>
                <Button type="button" variant="success" onClick={() => onTestAuth(profile)}>
                  Test Authentication
                </Button>
                {renderTestResultBadge(authTestResults, 'Auth Test')}
              </div>
            )}
          </div>
        ) : null}
      </form>
    </div>
  );
};
